/* eslint-disable react/no-array-index-key */
import {
  AccordionDetails,
  Box,
  Grid,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import { useState, useRef, useContext } from 'react';
import { useParams } from 'react-router-dom';

import {
  convertCriteriaGroupActivity,
  StatusCode,
} from '../../../../../../api/enumerations';
import { deleteCriteriasGroup } from '../../../../../../api/theHive/criteriaGroups';
import {
  RemoveCriteriaGroupData,
  TypologyAndCriteriaData,
} from '../../../../../../api/theHive/criteriaGroups/types';
import { ListTypologyData } from '../../../../../../api/theHive/typology/types';
import {
  IconExpandMoreMS,
  IconDeleteMS,
  IconEditMS,
} from '../../../../../../constants/icons';
import { GlobalContext } from '../../../../../../context/global';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import { AddTypologyModal } from './components/AddTypologyModal';
import { CriteriasTable } from './components/CriteriasTable';
import { EditGroupModal } from './components/EditGroupModal';
import { EditTypologyModal } from './components/EditTypologyModal';
import {
  CriteriaContainer,
  CriteriasContainerGrid,
  CriterionLabel,
  NoContentText,
  StyledAccordionSummary,
  StyledAccordion,
  AccordionTitle,
  DisplacementText,
  DisplacementValue,
  ContentContainer as StyledContentContainer,
  ScrollableGrid,
  InfoBox,
  ItemTitle,
} from './styles';
import { TypologyGroupGridProps } from './types';

export function TypologyGroupSection({
  criteriasGroup,
  fetchTypologyGroups,
  isLoading,
}: TypologyGroupGridProps): JSX.Element {
  const [loadingOnDelete, setLoadingOnDelete] = useState(false);
  const [expandedPanel, setExpandedPanel] = useState<number | null>(null);
  const [editTypologyGroup, setEditTypologyGroup] =
    useState<ListTypologyData>();
  const [editGroup, setEditGroup] = useState<TypologyAndCriteriaData>();

  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);
  const { setOpenSnackbar, setErrorMessage, setSnackbarMessage } =
    useContext(GlobalContext);
  const { getErrorMessage } = useErrorMessage();

  const scrollableGridRef = useRef<HTMLDivElement>(null);

  const handleAccordionChange =
    (id: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? id : null);
    };

  const handleDeleteCriteriaGroup = async (
    criteriaGroupId: number
  ): Promise<void> => {
    try {
      setLoadingOnDelete(true);
      const data: RemoveCriteriaGroupData = {
        noticeId,
        criteriaGroupId,
      };
      const response = await deleteCriteriasGroup(data);

      if (response.detail.description)
        throw new Error(response.detail.description);
      if (response.detail.status_code !== StatusCode.OK)
        throw new Error('Algo deu errado, tente novamente.');

      setSnackbarMessage('Serviço excluído com sucesso!');
      setErrorMessage(false);
      setOpenSnackbar(true);
      fetchTypologyGroups();
    } catch (error) {
      setSnackbarMessage(getErrorMessage(error));
      setOpenSnackbar(true);
      setErrorMessage(true);
    } finally {
      setLoadingOnDelete(false);
    }
  };

  if (isLoading || loadingOnDelete) {
    return (
      <CriteriasContainerGrid item container xs={12} mt={2}>
        <Grid item container>
          <Skeleton
            width={200}
            height={32}
            style={{
              backgroundColor: '#fafcff',
              boxShadow: '0px 3px 6px #00000029',
            }}
          />
        </Grid>
        <ScrollableGrid item container mt={2}>
          {[1, 2].map((_, index) => (
            <Skeleton
              style={{
                backgroundColor: '#fafcff',
                boxShadow: '0px 3px 6px #00000029',
              }}
              key={index}
              variant="rectangular"
              width="98%"
              height={45}
              sx={{ mb: 2, borderRadius: 1 }}
            />
          ))}
        </ScrollableGrid>
      </CriteriasContainerGrid>
    );
  }
  return (
    <>
      <CriteriasContainerGrid item container xs={12} mt={2}>
        <Grid
          item
          xs={12}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography fontSize={21}>Grupos de serviço</Typography>
          <AddTypologyModal fetchTypologyGroups={fetchTypologyGroups} />
        </Grid>
        <ScrollableGrid item container mt={2} ref={scrollableGridRef}>
          {criteriasGroup?.length === 0 ? (
            <NoContentText>Nenhum serviço adicionado</NoContentText>
          ) : (
            criteriasGroup.map((group) => (
              <StyledAccordion
                key={group.id}
                expanded={expandedPanel === group.id}
                onChange={handleAccordionChange(group.id)}
              >
                <StyledAccordionSummary>
                  <Grid
                    container
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item container alignItems="center" xs spacing={1}>
                      <AccordionTitle>{group.name}</AccordionTitle>
                      <span className="expandIcon">{IconExpandMoreMS}</span>
                    </Grid>

                    <Grid item>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditGroup(group);
                        }}
                        color="primary"
                        title="editar grupo"
                        sx={{ '&:hover': { color: 'secondary.main' } }}
                      >
                        {IconEditMS}
                      </IconButton>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteCriteriaGroup(group.id);
                        }}
                        color="primary"
                        title="excluir grupo"
                        sx={{ '&:hover': { color: 'secondary.main' } }}
                      >
                        {IconDeleteMS}
                      </IconButton>
                    </Grid>
                  </Grid>
                </StyledAccordionSummary>
                <AccordionDetails sx={{ padding: 0 }}>
                  <StyledContentContainer>
                    <InfoBox>
                      <DisplacementText>
                        • Conceito de espaço:
                        <DisplacementValue>
                          {convertCriteriaGroupActivity(group.activity)}
                        </DisplacementValue>
                      </DisplacementText>
                    </InfoBox>
                    <CriteriasTable
                      criteriasData={group}
                      onEditService={setEditTypologyGroup}
                    />
                    {(group.criterias ?? []).length > 0 && (
                      <CriteriaContainer>
                        <ItemTitle>Critérios:</ItemTitle>
                        {(group.criterias ?? []).map((criteria, index) => (
                          <Box key={criteria.id} margin="8px 40px 8px 16px">
                            <CriterionLabel>
                              {' '}
                              • Critério {index + 1}
                            </CriterionLabel>
                            <Typography textAlign="justify" ml={1}>
                              {criteria.description}
                            </Typography>
                          </Box>
                        ))}
                      </CriteriaContainer>
                    )}
                  </StyledContentContainer>
                </AccordionDetails>
              </StyledAccordion>
            ))
          )}
        </ScrollableGrid>
      </CriteriasContainerGrid>
      {editTypologyGroup && (
        <EditTypologyModal
          fetchTypologyGroups={fetchTypologyGroups}
          typologyGroup={editTypologyGroup}
          open={!!editTypologyGroup}
          handleClose={() => {
            setEditTypologyGroup(undefined);
          }}
        />
      )}
      {editGroup && (
        <EditGroupModal
          fetchTypologyGroups={fetchTypologyGroups}
          group={editGroup}
          open={!!editGroup}
          handleClose={() => {
            setEditGroup(undefined);
          }}
        />
      )}
    </>
  );
}
